import { AnalyticsService } from '@alison/analytics/client';
import GridViewOutlined from '@mui/icons-material/GridViewOutlined';
import TableRowsOutlined from '@mui/icons-material/TableRowsOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { type ReactNode, useMemo } from 'react';
import { useCreativeDetailSearch } from '../../lib/creative-detail-search.js';
import { useLeadingKpiOptions } from '../../lib/leadingKpi.hooks';
import { useMediaSearch } from '../../lib/media-search';
import { CreativesGrid } from './CreativesGrid';
import { CreativesTable } from './CreativesTable';
import { DisplayedMetrics } from './DisplayedMetrics';
import {
  type CreativeScoresScope,
  type CreativesListScope,
  mergeCreativeScores,
  sortByCreativeScore,
  sortByField,
  useCreativeScores,
  useCreativesList,
  useMetricCatalog,
} from './data/api';
import { creativesToCsv, downloadCsv } from './data/csv';
import {
  ACTIVE_DAYS_SORT_FIELD,
  CREATIVE_ID_SORT_FIELD,
  CREATIVE_SCORE_METRIC_NAME,
  ensureMetricFirst,
  leadWithMetric,
  nextSort,
  resolveDisplayed,
  SPEND_RATIO_SORT_FIELD,
} from './data/displayedMetrics';
import { CreativeDetailDrawer } from './detail/CreativeDetailDrawer.js';
import { ExportCsvButton } from './ExportCsvButton';
import { SortControl } from './SortControl';
import { TOGGLE_BUTTON_SX } from './utils';

interface CreativesBoardProps {
  accountIds: number[];
  dateWindow: [string, string];
  filters: Record<string, string[]> | undefined;
  route: '/explore';
  leadingKpi: string;
}

export function CreativesBoard({
  accountIds,
  dateWindow,
  filters,
  route,
  leadingKpi,
}: CreativesBoardProps): ReactNode {
  const { data: leadingKpiOptions } = useLeadingKpiOptions(accountIds);
  const leadingKpiLabel =
    leadingKpiOptions?.find((option) => option.id === leadingKpi)?.label ??
    leadingKpi.toUpperCase();
  const { data: catalog, error: catalogError } = useMetricCatalog(accountIds);
  const { displayedMetrics, rankedFor, setDisplayedMetrics, view, setView } = useMediaSearch(route);
  // A saved sort holds while its leading KPI does; otherwise the board ranks by the leading KPI.
  const applied = useMemo(() => {
    const resolved = resolveDisplayed(catalog, displayedMetrics);
    const keepsSort = displayedMetrics !== undefined && (rankedFor ?? leadingKpi) === leadingKpi;
    return keepsSort
      ? ensureMetricFirst(resolved, catalog, leadingKpi)
      : leadWithMetric(resolved, catalog, leadingKpi);
  }, [catalog, displayedMetrics, rankedFor, leadingKpi]);

  const wantsCreativeScore = applied.metrics.includes(CREATIVE_SCORE_METRIC_NAME);

  const listKpis = useMemo(
    () => applied.metrics.filter((name) => name !== CREATIVE_SCORE_METRIC_NAME),
    [applied.metrics],
  );
  const sortsClientSide =
    applied.sort === CREATIVE_SCORE_METRIC_NAME ||
    applied.sort === ACTIVE_DAYS_SORT_FIELD ||
    applied.sort === SPEND_RATIO_SORT_FIELD;
  const listSortField = sortsClientSide ? (listKpis[0] ?? CREATIVE_ID_SORT_FIELD) : applied.sort;

  const baseScope = useMemo<CreativeScoresScope>(
    () => ({
      dateRange: dateWindow,
      leadingKpi,
      ...(accountIds.length ? { accountIds } : {}),
      ...(filters ? { filters } : {}),
    }),
    [accountIds, dateWindow, filters, leadingKpi],
  );

  const scope = useMemo<CreativesListScope>(
    () => ({
      ...baseScope,
      kpis: listKpis,
      sort: { field: listSortField, direction: applied.direction },
    }),
    [baseScope, applied.direction, listKpis, listSortField],
  );

  const list = useCreativesList(scope, accountIds.length > 0 && listKpis.length > 0);
  const failure = catalogError ?? list.error;

  const detail = useCreativeDetailSearch(route);
  const detailScope = baseScope;

  const trendScope = useMemo(() => {
    const { kpis: _kpis, sort: _sort, ...rest } = scope;
    return rest;
  }, [scope]);

  const scoreScope = baseScope;

  const scores = useCreativeScores(scoreScope, accountIds.length > 0 && wantsCreativeScore);
  const dataWithScores = useMemo(() => {
    const merged = mergeCreativeScores(list.data, scores.data, wantsCreativeScore);
    if (merged === undefined) return merged;
    if (applied.sort === CREATIVE_SCORE_METRIC_NAME) {
      return sortByCreativeScore(merged, applied.direction);
    }
    if (applied.sort === ACTIVE_DAYS_SORT_FIELD) {
      return sortByField(merged, applied.direction, (creative) => creative.activeDays);
    }
    if (applied.sort === SPEND_RATIO_SORT_FIELD) {
      return sortByField(merged, applied.direction, (creative) => creative.spendRatio);
    }
    return merged;
  }, [list.data, scores.data, wantsCreativeScore, applied.sort, applied.direction]);
  const scoresReady = !wantsCreativeScore || scores.data !== undefined;
  const exportReady = dataWithScores !== undefined && scoresReady;

  const changeSort = (field: string): void => {
    if (field === '') return;
    const next = nextSort(applied, catalog, field);
    AnalyticsService.track('Explore - Sort', { field, direction: next.direction });
    setDisplayedMetrics(next);
  };

  const exportCsv = (): void => {
    if (!exportReady || dataWithScores === undefined) return;
    AnalyticsService.track('Explore - Export CSV');
    downloadCsv(
      `creatives_${dateWindow[0]}_${dateWindow[1]}.csv`,
      creativesToCsv(dataWithScores, leadingKpiLabel),
    );
  };

  const openCreative = (creativeId: string): void => {
    AnalyticsService.track('Explore - Open Creative', { creativeId });
    detail.open(creativeId);
  };

  return (
    <Box sx={{ px: 6, py: 4 }}>
      <Typography variant="h2" sx={{ mb: 4 }}>
        All Creatives
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DisplayedMetrics
            catalog={catalog}
            applied={applied}
            onChange={setDisplayedMetrics}
            surface="explore"
          />
          <SortControl
            catalog={catalog}
            applied={applied}
            leadingKpi={leadingKpi}
            leadingKpiLabel={leadingKpiLabel}
            onChange={setDisplayedMetrics}
          />
          <ExportCsvButton
            dataTest="creatives-export-csv"
            disabled={!exportReady}
            onClick={exportCsv}
          />
        </Box>
        <ToggleButtonGroup
          exclusive
          value={view}
          onChange={(_event, next: unknown) => {
            if (next === 'table' || next === 'grid') {
              AnalyticsService.track('Explore - Change View', { view: next });
              setView(next);
            }
          }}
          sx={{ backgroundColor: 'background.page' }}
        >
          <ToggleButton value="grid" sx={TOGGLE_BUTTON_SX}>
            <GridViewOutlined sx={{ fontSize: 16, mr: 1 }} />
            Grid
          </ToggleButton>
          <ToggleButton value="table" sx={TOGGLE_BUTTON_SX}>
            <TableRowsOutlined sx={{ fontSize: 16, mr: 1 }} />
            Table
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      {failure ? (
        <Alert severity="error" sx={{ mt: 4 }}>
          {failure.message}
        </Alert>
      ) : (
        <Box sx={{ mt: 4 }}>
          {view === 'grid' ? (
            <CreativesGrid
              data={dataWithScores}
              catalog={catalog}
              applied={applied}
              pending={list.isPending}
              onOpen={openCreative}
              leadingKpiLabel={leadingKpiLabel}
            />
          ) : (
            <CreativesTable
              data={dataWithScores}
              catalog={catalog}
              applied={applied}
              pending={list.isPending}
              trendScope={trendScope}
              onSortChange={changeSort}
              onOpen={openCreative}
              leadingKpiLabel={leadingKpiLabel}
            />
          )}
        </Box>
      )}
      <CreativeDetailDrawer
        creatives={dataWithScores?.creatives ?? []}
        creativeId={detail.creativeId}
        tab={detail.tab}
        scope={detailScope}
        kpis={listKpis}
        leadingKpiLabel={leadingKpiLabel}
        onOpen={detail.open}
        onTabChange={detail.setTab}
        onClose={detail.close}
      />
    </Box>
  );
}
